import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FileText, Eye, Edit2, Trash2,
  CheckCircle, XCircle, Download, CreditCard, Clock, IndianRupee, Package, User,
  MoreVertical, RefreshCw,
} from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import { salesReturnsAPI } from '../../services/part2-api';
import ColumnFilter from '../../components/shared/ColumnFilter';
import ListPageHeader from '../../components/shared/ListPageHeader';
import StaffFilterSelect from '../../components/shared/StaffFilterSelect';
import ConfirmModal from '../../components/modals/ConfirmModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getApiErrorMessage } from '../../utils/apiErrors';

// Status -> badge config map. Post-2026-04-17-sweep vocabulary is
// DRAFT / CONFIRMED / COMPLETED / CANCELLED (the legacy PENDING /
// APPROVED / REJECTED / REFUNDED on SalesReturnsPage.js was never the
// backend's shape and is being left untouched per user instruction).
const STATUS_BADGES = {
  DRAFT:     { bg: 'bg-slate-100',   text: 'text-slate-700',   label: 'Draft' },
  CONFIRMED: { bg: 'bg-blue-100',    text: 'text-blue-700',    label: 'Confirmed' },
  COMPLETED: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Completed' },
  CANCELLED: { bg: 'bg-red-100',     text: 'text-red-700',     label: 'Cancelled' },
};

const TYPE_BADGES = {
  credit_note: { label: 'Credit Note', color: 'text-blue-600' },
  refund:      { label: 'Refund',      color: 'text-emerald-600' },
  exchange:    { label: 'Exchange',    color: 'text-purple-600' },
  stock_only:  { label: 'Stock Only',  color: 'text-slate-600' },
};

const SalesReturnsListPage = () => {
  const navigate = useNavigate();
  const { businessId } = useParams();
  const { activeBusiness } = useBusiness();
  const [returns, setReturns] = useState([]);
  const [stats, setStats] = useState({
    total: 0, draft: 0, confirmed: 0, completed: 0,
    total_value: 0, total_refunded: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [confirmModal, setConfirmModal] = useState(null);

  // Role-based visibility
  const [isOwner, setIsOwner] = useState(true);
  const [staffList, setStaffList] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState(null);

  const [columnFilters, setColumnFilters] = useState({
    return_no: '',
    return_date: { min: '', max: '' },
    customer_name: '',
    linked_invoice_no: '',
    return_type: [],
    amount: { min: '', max: '' },
    status: [],
    staff_member: '',
  });

  const bid = businessId || activeBusiness?.id;
  const businessPath = bid ? `/business/${bid}` : '';

  const fetchReturns = useCallback(async () => {
    if (!bid) return;
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter.toUpperCase();
      if (typeFilter !== 'all') params.return_type = typeFilter;
      if (searchQuery) params.search = searchQuery;
      if (selectedStaffId) params.staff_id = selectedStaffId;

      const response = await salesReturnsAPI.list(params);
      setReturns(response.data.returns || []);
      setStats(response.data.stats || { total: 0, draft: 0, confirmed: 0, completed: 0, total_value: 0, total_refunded: 0 });
      setIsOwner(response.data.is_owner !== false);
      setStaffList(response.data.staff_list || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load sales returns'));
    } finally {
      setLoading(false);
    }
  }, [bid, statusFilter, typeFilter, searchQuery, selectedStaffId]);

  useEffect(() => {
    fetchReturns();
  }, [fetchReturns]);

  // ────────────────────── Action handlers (all via ConfirmModal) ──────────────

  const handleConfirmReturn = (ret) => {
    setConfirmModal({
      title: 'Confirm Sales Return',
      description: `Confirm ${ret.return_no || ret.return_number}? This will update inventory for restocked items.`,
      confirmLabel: 'Confirm Return',
      confirmClass: 'bg-emerald-600 hover:bg-emerald-700',
      icon: CheckCircle,
      iconClass: 'text-emerald-600',
      iconBgClass: 'bg-emerald-100',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await salesReturnsAPI.confirm(ret.id);
          toast.success('Sales return confirmed');
          fetchReturns();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to confirm return'));
        }
      },
    });
  };

  const handleCancelReturn = (ret) => {
    const willReverseStock = ret.status === 'CONFIRMED' || ret.status === 'COMPLETED';
    setConfirmModal({
      title: 'Cancel Sales Return',
      description: willReverseStock
        ? `Cancel ${ret.return_no || ret.return_number}? Inventory restock adjustments will be reversed.`
        : `Cancel ${ret.return_no || ret.return_number}?`,
      confirmLabel: 'Cancel Return',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: XCircle,
      iconClass: 'text-red-600',
      iconBgClass: 'bg-red-100',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await salesReturnsAPI.cancel(ret.id);
          toast.success('Sales return cancelled');
          fetchReturns();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to cancel return'));
        }
      },
    });
  };

  const handleDelete = (ret) => {
    setConfirmModal({
      title: 'Delete Sales Return',
      description: `Delete draft ${ret.return_no || ret.return_number}? This cannot be undone.`,
      confirmLabel: 'Delete',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: Trash2,
      iconClass: 'text-red-600',
      iconBgClass: 'bg-red-100',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await salesReturnsAPI.delete(ret.id);
          toast.success('Sales return deleted');
          fetchReturns();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to delete return'));
        }
      },
    });
  };

  const handleDownloadPDF = async (ret) => {
    try {
      const response = await salesReturnsAPI.downloadPdf(ret.id);
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SalesReturn_${ret.return_no || ret.return_number || ret.id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to download PDF'));
    }
  };

  const handleCreateCreditNote = (ret) => {
    setConfirmModal({
      title: 'Create Credit Note',
      description: `Issue a credit note for ${ret.return_no || ret.return_number}? The return will be marked as completed.`,
      confirmLabel: 'Create Credit Note',
      confirmClass: 'bg-blue-600 hover:bg-blue-700',
      icon: CreditCard,
      iconClass: 'text-blue-600',
      iconBgClass: 'bg-blue-100',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const response = await salesReturnsAPI.createCreditNote(ret.id);
          const cn = response.data?.credit_note;
          toast.success(`Credit note ${cn?.credit_note_number || ''} created`);
          fetchReturns();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to create credit note'));
        }
      },
    });
  };

  const handleRecordRefund = (ret) => {
    // Quick-confirm full-amount refund in CASH. The detail page has a
    // richer form for partial refunds / different methods.
    const amount = Number(ret.total ?? ret.grand_total ?? 0);
    setConfirmModal({
      title: 'Record Refund',
      description: `Record a full CASH refund of ₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })} for ${ret.return_no || ret.return_number}? Use the detail page for partial refunds or other payment methods.`,
      confirmLabel: 'Record Refund',
      confirmClass: 'bg-emerald-600 hover:bg-emerald-700',
      icon: IndianRupee,
      iconClass: 'text-emerald-600',
      iconBgClass: 'bg-emerald-100',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await salesReturnsAPI.recordRefund(ret.id, { amount, method: 'CASH' });
          toast.success('Refund recorded');
          fetchReturns();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to record refund'));
        }
      },
    });
  };

  const handleViewReturn = (ret) => {
    navigate(`${businessPath}/sales-returns/${ret.id}`);
  };

  const handleEditReturn = (ret) => {
    navigate(`${businessPath}/sales-returns/${ret.id}/edit`);
  };

  const formatCompactCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount?.toFixed(0) || 0}`;
  };

  const getStatusBadge = (status) => STATUS_BADGES[status] || STATUS_BADGES.DRAFT;
  const getTypeBadge = (type) => TYPE_BADGES[type] || { label: type || '—', color: 'text-slate-600' };

  // Backend aliases party_name -> customer_name already, but keep a
  // fallback during the transition in case any cached responses still
  // carry the legacy name only.
  const getCustomerName = (r) => r.customer_name || r.party_name || '';
  const getReturnNo = (r) => r.return_no || r.return_number || '';
  const getLinkedInvoiceNo = (r) => r.linked_invoice_no || r.linked_invoice_number || '';
  const getTotal = (r) => r.total ?? r.grand_total ?? 0;

  const filteredReturns = returns.filter((ret) => {
    const matchesSearch = !searchQuery ||
      getReturnNo(ret).toLowerCase().includes(searchQuery.toLowerCase()) ||
      getCustomerName(ret).toLowerCase().includes(searchQuery.toLowerCase()) ||
      getLinkedInvoiceNo(ret).toLowerCase().includes(searchQuery.toLowerCase());

    const matchesReturnNo = !columnFilters.return_no ||
      getReturnNo(ret).toLowerCase().includes(columnFilters.return_no.toLowerCase());

    const matchesReturnDate = (!columnFilters.return_date?.min || new Date(ret.return_date) >= new Date(columnFilters.return_date.min)) &&
      (!columnFilters.return_date?.max || new Date(ret.return_date) <= new Date(columnFilters.return_date.max));

    const matchesCustomer = !columnFilters.customer_name ||
      getCustomerName(ret).toLowerCase().includes(columnFilters.customer_name.toLowerCase());

    const matchesInvoice = !columnFilters.linked_invoice_no ||
      getLinkedInvoiceNo(ret).toLowerCase().includes(columnFilters.linked_invoice_no.toLowerCase());

    const matchesType = columnFilters.return_type.length === 0 ||
      columnFilters.return_type.includes(ret.return_type);

    const matchesAmount = (!columnFilters.amount?.min || getTotal(ret) >= parseFloat(columnFilters.amount.min)) &&
      (!columnFilters.amount?.max || getTotal(ret) <= parseFloat(columnFilters.amount.max));

    const matchesStatus = columnFilters.status.length === 0 ||
      columnFilters.status.includes(ret.status);

    const matchesStaff = !columnFilters.staff_member ||
      (ret.created_by_staff_name || 'Owner').toLowerCase().includes(columnFilters.staff_member.toLowerCase());

    return matchesSearch && matchesReturnNo && matchesReturnDate && matchesCustomer &&
           matchesInvoice && matchesType && matchesAmount && matchesStatus && matchesStaff;
  });

  return (
    <>
    <div className="h-full flex flex-col" data-testid="sales-returns-page">
      <ListPageHeader
        title="Sales Returns"
        stats={[
          { icon: FileText,    label: 'Total',        value: stats.total,        color: 'slate' },
          { icon: Clock,       label: 'Draft',        value: stats.draft,        color: 'amber' },
          { icon: CheckCircle, label: 'Confirmed',    value: stats.confirmed,    color: 'blue' },
          { icon: IndianRupee, label: 'Return Value', value: formatCompactCurrency(stats.total_value),    color: 'emerald' },
          { icon: CreditCard,  label: 'Refunded',     value: formatCompactCurrency(stats.total_refunded), color: 'rose' },
        ]}
        ctaLabel="New Sales Return"
        onCtaClick={() => navigate(`${businessPath}/sales-returns/new`)}
        extraControls={isOwner && staffList.length > 0 ? (
          <StaffFilterSelect
            staffList={staffList}
            selectedStaffId={selectedStaffId}
            onChange={setSelectedStaffId}
          />
        ) : null}
      />

      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
                <p className="text-[13px] text-slate-500 font-normal">Loading returns...</p>
              </div>
            </div>
          ) : filteredReturns.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <Package className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">
                {searchQuery ? 'No returns found' : 'No sales returns yet'}
              </p>
              <p className="text-[13px] text-slate-400 mt-1 mb-4">
                {searchQuery ? 'Try adjusting your search' : 'Create your first sales return to get started'}
              </p>
              {!searchQuery && (
                <button
                  onClick={() => navigate(`${businessPath}/sales-returns/new`)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-[13px] font-medium"
                >
                  Create Sales Return
                </button>
              )}
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto">
                <table className="tl-table" style={{ tableLayout: 'fixed' }}>
                  <thead className="sticky top-0 z-10 bg-white">
                    <tr>
                      <th className="tl-th bg-blue-50/60 text-blue-700 border-b border-blue-100" style={{ width: isOwner ? '10%' : '12%' }}>
                        <div className="flex items-center gap-1">
                          Return #
                          <ColumnFilter
                            type="text"
                            value={columnFilters.return_no}
                            onChange={(val) => setColumnFilters({ ...columnFilters, return_no: val })}
                            placeholder="Filter return..."
                          />
                        </div>
                      </th>
                      <th className="tl-th bg-blue-50/60 text-blue-700 border-b border-blue-100" style={{ width: '9%' }}>
                        <div className="flex items-center gap-1">
                          Date
                          <ColumnFilter
                            type="date"
                            value={columnFilters.return_date}
                            onChange={(val) => setColumnFilters({ ...columnFilters, return_date: val })}
                          />
                        </div>
                      </th>
                      <th className="tl-th bg-blue-50/60 text-blue-700 border-b border-blue-100" style={{ width: isOwner ? '14%' : '18%' }}>
                        <div className="flex items-center gap-1">
                          Customer
                          <ColumnFilter
                            type="text"
                            value={columnFilters.customer_name}
                            onChange={(val) => setColumnFilters({ ...columnFilters, customer_name: val })}
                            placeholder="Filter customer..."
                          />
                        </div>
                      </th>
                      <th className="tl-th bg-blue-50/60 text-blue-700 border-b border-blue-100" style={{ width: '10%' }}>
                        <div className="flex items-center gap-1">
                          Invoice
                          <ColumnFilter
                            type="text"
                            value={columnFilters.linked_invoice_no}
                            onChange={(val) => setColumnFilters({ ...columnFilters, linked_invoice_no: val })}
                            placeholder="Filter invoice..."
                          />
                        </div>
                      </th>
                      <th className="tl-th bg-blue-50/60 text-blue-700 border-b border-blue-100" style={{ width: '9%' }}>
                        <div className="flex items-center gap-1">
                          Type
                          <ColumnFilter
                            type="status"
                            value={columnFilters.return_type}
                            onChange={(val) => setColumnFilters({ ...columnFilters, return_type: val })}
                            options={[
                              { value: 'credit_note', label: 'Credit Note' },
                              { value: 'refund', label: 'Refund' },
                              { value: 'exchange', label: 'Exchange' },
                              { value: 'stock_only', label: 'Stock Only' },
                            ]}
                          />
                        </div>
                      </th>
                      {isOwner && (
                        <th className="tl-th bg-blue-50/60 text-blue-700 border-b border-blue-100" style={{ width: '12%' }}>
                          <div className="flex items-center gap-1">
                            <User className="w-3.5 h-3.5" />
                            Staff
                            <ColumnFilter
                              type="text"
                              value={columnFilters.staff_member}
                              onChange={(val) => setColumnFilters({ ...columnFilters, staff_member: val })}
                              placeholder="Filter staff..."
                            />
                          </div>
                        </th>
                      )}
                      <th className="tl-th bg-blue-50/60 text-blue-700 border-b border-blue-100 tl-th-right" style={{ width: '10%' }}>
                        <div className="flex items-center justify-end gap-1">
                          Amount
                          <ColumnFilter
                            type="number"
                            value={columnFilters.amount}
                            onChange={(val) => setColumnFilters({ ...columnFilters, amount: val })}
                          />
                        </div>
                      </th>
                      <th className="tl-th bg-blue-50/60 text-blue-700 border-b border-blue-100" style={{ width: '9%' }}>
                        <div className="flex items-center gap-1">
                          Status
                          <ColumnFilter
                            type="status"
                            value={columnFilters.status}
                            onChange={(val) => setColumnFilters({ ...columnFilters, status: val })}
                            options={[
                              { value: 'DRAFT', label: 'Draft' },
                              { value: 'CONFIRMED', label: 'Confirmed' },
                              { value: 'COMPLETED', label: 'Completed' },
                              { value: 'CANCELLED', label: 'Cancelled' },
                            ]}
                          />
                        </div>
                      </th>
                      <th className="tl-th bg-blue-50/60 text-blue-700 border-b border-blue-100" style={{ width: '60px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReturns.map((ret, index) => {
                      const statusBadge = getStatusBadge(ret.status);
                      const typeBadge = getTypeBadge(ret.return_type);
                      const isDraft = ret.status === 'DRAFT';
                      const isConfirmed = ret.status === 'CONFIRMED';
                      const isCreditNoteType = ret.return_type === 'credit_note';
                      const isRefundType = ret.return_type === 'refund';
                      const hasCreditNote = !!ret.credit_note_id;
                      return (
                        <tr
                          key={ret.id}
                          className={`tl-tr tl-tr-clickable hover:bg-blue-50/40 ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                          onClick={() => handleViewReturn(ret)}
                        >
                          <td className="tl-td" style={{ width: isOwner ? '10%' : '12%' }}>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-[12px] font-medium text-blue-700">
                                  {getReturnNo(ret).slice(-2) || 'SR'}
                                </span>
                              </div>
                              <span className="tl-cell-primary">{getReturnNo(ret)}</span>
                            </div>
                          </td>
                          <td className="tl-td tl-cell-regular" style={{ width: '9%' }}>
                            {ret.return_date ? new Date(ret.return_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}
                          </td>
                          <td className="tl-td tl-cell-regular" style={{ width: isOwner ? '14%' : '18%' }}>
                            {getCustomerName(ret) || '—'}
                          </td>
                          <td className="tl-td tl-cell-regular" style={{ width: '10%' }}>
                            {getLinkedInvoiceNo(ret) || '—'}
                          </td>
                          <td className="tl-td" style={{ width: '9%' }}>
                            <span className={`text-[11px] font-medium ${typeBadge.color}`}>
                              {typeBadge.label}
                            </span>
                          </td>
                          {isOwner && (
                            <td className="tl-td" style={{ width: '12%' }}>
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                                  <User className="w-3 h-3 text-slate-500" />
                                </div>
                                <span className="text-[12px] text-slate-600 truncate">
                                  {ret.created_by_staff_name || 'Owner'}
                                </span>
                              </div>
                            </td>
                          )}
                          <td className="tl-td tl-td-right tl-cell-regular" style={{ width: '10%' }}>
                            ₹{getTotal(ret).toLocaleString('en-IN')}
                          </td>
                          <td className="tl-td" style={{ width: '9%' }}>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                              {statusBadge.label}
                            </span>
                          </td>
                          <td className="tl-td" style={{ width: '60px' }} onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                                  title="Actions"
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-52">
                                <DropdownMenuItem onClick={() => handleViewReturn(ret)}>
                                  <Eye className="w-4 h-4 mr-2 text-slate-500" />
                                  View
                                </DropdownMenuItem>

                                {isDraft && (
                                  <DropdownMenuItem onClick={() => handleEditReturn(ret)}>
                                    <Edit2 className="w-4 h-4 mr-2 text-slate-500" />
                                    Edit
                                  </DropdownMenuItem>
                                )}
                                {isDraft && (
                                  <DropdownMenuItem onClick={() => handleConfirmReturn(ret)}>
                                    <CheckCircle className="w-4 h-4 mr-2 text-emerald-600" />
                                    Confirm
                                  </DropdownMenuItem>
                                )}

                                {isConfirmed && isCreditNoteType && !hasCreditNote && (
                                  <DropdownMenuItem onClick={() => handleCreateCreditNote(ret)}>
                                    <CreditCard className="w-4 h-4 mr-2 text-blue-600" />
                                    Create Credit Note
                                  </DropdownMenuItem>
                                )}
                                {isConfirmed && isRefundType && !ret.refund_processed && (
                                  <DropdownMenuItem onClick={() => handleRecordRefund(ret)}>
                                    <IndianRupee className="w-4 h-4 mr-2 text-emerald-600" />
                                    Record Refund
                                  </DropdownMenuItem>
                                )}

                                <DropdownMenuItem onClick={() => handleDownloadPDF(ret)}>
                                  <Download className="w-4 h-4 mr-2 text-slate-500" />
                                  Download PDF
                                </DropdownMenuItem>

                                {isDraft && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => handleDelete(ret)}
                                      className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {(isConfirmed || ret.status === 'COMPLETED') && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => handleCancelReturn(ret)}
                                      className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                    >
                                      <XCircle className="w-4 h-4 mr-2" />
                                      Cancel
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                <p>
                  Showing <span className="font-medium text-slate-700">{filteredReturns.length}</span> returns
                </p>
                <p>
                  Total Value: <span className="font-medium text-slate-700">{formatCompactCurrency(stats.total_value || 0)}</span>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

    <ConfirmModal
      isOpen={!!confirmModal}
      title={confirmModal?.title}
      description={confirmModal?.description}
      confirmLabel={confirmModal?.confirmLabel}
      confirmClass={confirmModal?.confirmClass}
      icon={confirmModal?.icon}
      iconClass={confirmModal?.iconClass}
      iconBgClass={confirmModal?.iconBgClass}
      onConfirm={() => confirmModal?.onConfirm?.()}
      onClose={() => setConfirmModal(null)}
    />
    </>
  );
};

export default SalesReturnsListPage;
